import XLSX from 'xlsx';
import { BaseParser,
        DATA_TYPE_DATA,
        TYPE_2D,
        TYPE_PSSESSION,
        RE_TIME_HEADER_FIRST,
        RE_POTENTIAL_METROHM_HEADER_FIRST,
        RE_CURRENT_HEADER_FIRST,
        RE_SCAN_HEADER_FIRST } from './baseExtractor';

const iconXLSX = 'XLSXFile.png';

// Regex for extraction
const reMesasurementsSheet = RegExp('^measurements_\\d');
const reCellContrentration = RegExp('^([A-Z]+)1$');
const reHeaders = RegExp('^([A-Z]+)1$');
const reXLSXExt = /(.*)\.xlsx$/i;

class XLSXExtractor extends BaseParser {
  // eslint-disable-next-line no-unused-vars
  async _doParse(parent, fileName, data, report) {
    const returnValue = this.getNewReturnValue();
    try {
      const dataConvert = new Uint8Array(data);
      const workbook = XLSX.read(dataConvert, { type: 'array' });
      const keySheetName = workbook.SheetNames.find((sheetN) => reMesasurementsSheet.test(sheetN));
      if (typeof keySheetName === 'undefined') {
        returnValue.lastError = 'The Excel sheet does not contains measurements sheet';
        return this.tryAuxExcelFiles(fileName, data);
      }
      const measurSheet = workbook.Sheets[keySheetName];
      const concentrationCols = Object.keys(measurSheet).filter((cell) => reCellContrentration.test(cell));
      const concentrationColsOnlyCols = [];
      const objectConcExtracted = {};
      for (let j = 0; j < concentrationCols.length; j++) {
        const cellConcKeyOnlyCol = reCellContrentration.exec(concentrationCols[j])[1]; //Get the cell key
        concentrationColsOnlyCols.push(cellConcKeyOnlyCol);
        const cellConcKey = concentrationCols[j];
        const group = parseFloat(measurSheet[cellConcKey].v);
        if (isNaN(group)) {
          returnValue.warnings.push(`The value ${measurSheet[cellConcKey].v} is not a valid concentration. Cell ${cellConcKey} discarted`);
          continue;
        }
        objectConcExtracted[cellConcKeyOnlyCol] = {
          //Initialize the Object
          group: group,
          X: [],
          Y: [],
          fileName: fileName,
          title: `${group} - ${cellConcKeyOnlyCol}`,
          concUnit: '',
        };
      }
      if (concentrationColsOnlyCols.length === 0) {
        returnValue.lastError = 'The Excel sheet measurements does not have Concentration or equivalent information defined';
        return returnValue;
      }
      let minCol = concentrationCols[0]; //Select the first element
      for (let j = 1; j < concentrationCols.length; j++) {
        //Get the shortest
        if (minCol.length > concentrationCols[j].length && minCol > concentrationCols[j]) {
          minCol = concentrationCols[j];
        }
      }
      const timeCol = String.fromCharCode(minCol.charCodeAt(0) - 1);
      let line = 2;
      const cellObjectTime = measurSheet[timeCol + String(line)]; //Aux for optimization of the code in build
      let cellTime = typeof cellObjectTime !== 'undefined' ? parseFloat(cellObjectTime.v) : NaN; //Read first line
      while (!isNaN(cellTime)) {
        for (let f = 0; f < concentrationColsOnlyCols.length; f++) {
          //Try to get the value in Y
          const cellObjectConcVal = measurSheet[concentrationColsOnlyCols[f] + String(line)];
          const valueY = typeof cellObjectConcVal !== 'undefined' ? parseFloat(cellObjectConcVal.v) : NaN;
          if (!isNaN(valueY)) {
            objectConcExtracted[concentrationColsOnlyCols[f]].X.push(cellTime);
            objectConcExtracted[concentrationColsOnlyCols[f]].Y.push(valueY);
          }
        }
        line += 1; //Get new line
        const cellObjectTime2 = measurSheet[timeCol + String(line)];
        cellTime = typeof cellObjectTime2 !== 'undefined' ? parseFloat(cellObjectTime2.v) : NaN;
      }
  
      const extractedValues = Object.values(objectConcExtracted);
      for (let n = 0; n < extractedValues.length; n++) {
        if (extractedValues[n].X.length > 0) {
          returnValue.processedData.push(extractedValues[n]);
        }
      }
      returnValue.lastError = returnValue.processedData.length === 0 ? `File '${fileName}' is empty` : '';
      returnValue.dataType = DATA_TYPE_DATA;
      returnValue.type = TYPE_2D;
      returnValue.icon = iconXLSX;
      returnValue.success = returnValue.processedData.length > 0;
      if (!returnValue.success) {
        return this.tryAuxExcelFiles(fileName, data);
      }
      return returnValue;
    } catch (e) {
      return this.tryAuxExcelFiles(fileName, data);
    }
  }
  
  tryAuxExcelFiles(fileName, data) {
    if(this.isGalvanoPlot(fileName, data)) {
      return this.extractGalvanoPlot(fileName, data);
    }
    if (this.isBioLogics(fileName, data)) {
      return this.extractBioLogicsExcel(fileName, data);
    }
    return this.XLSXMetrohmAutolabFile(fileName, data);
  }
  
  XLSXMetrohmAutolabFile(fileName, data) {
    const returnValue = this.getNewReturnValue();
    const dataConvert = new Uint8Array(data);
    const dataExtract = this.getDataFromFileName(fileName, reXLSXExt); //Extract data from the file name
    const workbook = XLSX.read(dataConvert, { type: 'array' });
    //Initialize the return variable
    for (const [sheetName, measurSheet] of Object.entries(workbook.Sheets)) {
      const headers = Object.keys(measurSheet).filter((cell) => reHeaders.test(cell));
      let potentialCol = null;
      let currentCol = null;
      let timeCol = null;
      let scanCol = null;
      for (const header of headers) {
        // Check the headers and extract only the letter from the column
        if (RE_TIME_HEADER_FIRST.test(measurSheet[header].v)) {
          timeCol = header.substring(0, 1);
        } else if (RE_POTENTIAL_METROHM_HEADER_FIRST.test(measurSheet[header].v)) {
          potentialCol = header.substring(0, 1);
        } else if (RE_CURRENT_HEADER_FIRST.test(measurSheet[header].v)) {
          currentCol = header.substring(0, 1);
        } else if (RE_SCAN_HEADER_FIRST.test(measurSheet[header].v)) {
          scanCol = header.substring(0, 1);
        }
      }
      if (potentialCol === null || currentCol === null || timeCol === null) {
        continue;
      }

      //Initialize axuliar variables
      let line = 2;
      let currentScan = null;
      let newScan = null;
      if (scanCol !== null) {
        currentScan = typeof measurSheet[scanCol + String(line)] !== 'undefined' ? measurSheet[scanCol + String(line)].v
                                                                                  : null;
      }
      let title = currentScan !== null ? `${fileName}-${sheetName}-Scan${currentScan}` : `${fileName}-${sheetName}`;
      // Each scan has its own data line
      let currentDataline = {
        title,
        group: dataExtract.group,
        concUnit: dataExtract.concUnit,
        time: [],
        potential: [],
        current: [],
        fileName: fileName,
        date: '',
      };
      //Get the values of each element
      let cellTime = typeof measurSheet[timeCol + String(line)] !== 'undefined' ? parseFloat(measurSheet[timeCol + String(line)].v) : NaN;
      let cellPote = typeof measurSheet[potentialCol + String(line)] !== 'undefined' ? parseFloat(measurSheet[potentialCol + String(line)].v) : NaN;
      let cellCurr = typeof measurSheet[currentCol + String(line)] !== 'undefined' ? parseFloat(measurSheet[currentCol + String(line)].v) : NaN;
      while (!isNaN(cellTime) && !isNaN(cellPote) && !isNaN(cellCurr)) {
        //Add the values to the arrays
        currentDataline.time.push(cellTime);
        currentDataline.potential.push(cellPote);
        currentDataline.current.push(cellCurr);
        //Get new line
        line += 1;
        newScan = typeof measurSheet[scanCol + String(line)] !== 'undefined' ? measurSheet[scanCol + String(line)].v : null;
        cellTime = typeof measurSheet[timeCol + String(line)] !== 'undefined' ? parseFloat(measurSheet[timeCol + String(line)].v) : NaN;
        cellPote = typeof measurSheet[potentialCol + String(line)] !== 'undefined' ? parseFloat(measurSheet[potentialCol + String(line)].v) : NaN;
        cellCurr = typeof measurSheet[currentCol + String(line)] !== 'undefined' ? parseFloat(measurSheet[currentCol + String(line)].v) : NaN;
        if (newScan !== null && newScan !== currentScan) {
          // Save the scan
          returnValue.processedData.push(currentDataline);
          //Initilize the new Scan
          currentScan = newScan;
          title = `${fileName}-${sheetName}-Scan${currentScan}`;
          currentDataline = {
            title,
            group: dataExtract.group,
            concUnit: dataExtract.concUnit,
            time: [],
            potential: [],
            current: [],
            fileName: fileName,
            date: '',
          };
        }
      }
      if (currentDataline.time.length === 0) {
        continue;
      }
      returnValue.processedData.push(currentDataline);
      }
    returnValue.lastError = returnValue.processedData.length === 0 ? `File '${fileName}' is empty` : '';
    returnValue.dataType = DATA_TYPE_DATA;
    returnValue.type = TYPE_PSSESSION;
    returnValue.icon = iconXLSX;
    returnValue.success = returnValue.processedData.length > 0;
    return returnValue;
  }
  
  isGalvanoPlot(fileName, data) {
    // Verifies if the type of the file is type galvano plot
    try {
      const dataConvert = new Uint8Array(data);
      const workbook = XLSX.read(dataConvert, { type: 'array' });
      for (const measurSheet of Object.values(workbook.Sheets)) {
          const headValues = [];
          for (let j = 1; j <= 10; j++) {
              const cellObjt = measurSheet[`A${j}`];
              if (typeof cellObjt !== 'undefined') {
                  headValues.push(cellObjt.v);
              }
          }
          if (
              headValues.some((cell) => /vendor/i.test(cell)) &&
              headValues.some((cell) => /galvanoplot/i.test(cell)) &&
              headValues.some((cell) => /device/i.test(cell))
          )
              return true;
      }
      return false;
    } catch (e) {
      return false;
    }
  }
  
  extractGalvanoPlot(fileName, data) {
    const returnValue = this.getNewReturnValue();
    const dataConvert = new Uint8Array(data);
    const dataExtract = this.getDataFromFileName(fileName, reXLSXExt); //Extract data from the file name
    const workbook = XLSX.read(dataConvert, { type: 'array' });
    for (const [sheetName, measurSheet] of Object.entries(workbook.Sheets)) {
        //Find the headers of the data
        let dataPointRow = 5;
        for (; dataPointRow < 15; dataPointRow++) {
            const testheader = measurSheet[`A${dataPointRow}`];
            if (typeof testheader === 'undefined') {
                continue;
            }
            if (RE_TIME_HEADER_FIRST.test(testheader.v)) {
                break;
            }
        }
        if (dataPointRow === 15) {
            throw 'Header of Glavano Plot not found';
        }
        dataPointRow += 1; //Move to the data line
        const timeCol = 'A';
        const potentialCol = 'B';
        const currentCol = 'C';
        let title = `${fileName}-${sheetName}`;
        // Each scan has its own data line
        let currentDataline = {
            title,
            group: dataExtract.group,
            concUnit: dataExtract.concUnit,
            time: [],
            potential: [],
            current: [],
            fileName: fileName,
            date: '',
        };
        //Get the values of each element
        let cellTime = typeof measurSheet[timeCol + String(dataPointRow)] !== 'undefined' ? parseFloat(measurSheet[timeCol + String(dataPointRow)].v) : NaN;
        let cellPote = typeof measurSheet[potentialCol + String(dataPointRow)] !== 'undefined' ? parseFloat(measurSheet[potentialCol + String(dataPointRow)].v): NaN;
        let cellCurr = typeof measurSheet[currentCol + String(dataPointRow)] !== 'undefined' ? parseFloat(measurSheet[currentCol + String(dataPointRow)].v) : NaN;
        while (!isNaN(cellTime) && !isNaN(cellPote) && !isNaN(cellCurr)) {
          //Add the values to the arrays
          currentDataline.time.push(cellTime);
          currentDataline.potential.push(cellPote);
          currentDataline.current.push(cellCurr);
          //Get new line
          dataPointRow += 1;
          cellTime = typeof measurSheet[timeCol + String(dataPointRow)] !== 'undefined' ? parseFloat(measurSheet[timeCol + String(dataPointRow)].v) : NaN;
          cellPote = typeof measurSheet[potentialCol + String(dataPointRow)] !== 'undefined' ? parseFloat(measurSheet[potentialCol + String(dataPointRow)].v) : NaN;
          cellCurr = typeof measurSheet[currentCol + String(dataPointRow)] !== 'undefined' ? parseFloat(measurSheet[currentCol + String(dataPointRow)].v) : NaN;
        }
        if (currentDataline.time.length === 0) {
            continue;
        }
        returnValue.processedData.push(currentDataline);
    }
    returnValue.lastError = returnValue.processedData.length === 0 ? `File '${fileName}' is empty` : '';
    returnValue.dataType = DATA_TYPE_DATA;
    returnValue.type = TYPE_PSSESSION;
    returnValue.icon = iconXLSX;
    returnValue.success = returnValue.processedData.length > 0;
    return returnValue;
  }
  
  isBioLogics(fileName, data) {
    try {
      const dataConvert = new Uint8Array(data);
      const workbook = XLSX.read(dataConvert, { type: 'array' });
      for (const measurSheet of Object.values(workbook.Sheets)) {
        // Biologics have 2D data lines arrangesed as two adjacent coulmns, where the header cell is merged
        // for both columns, causing every other cell on row 1 to be undefined
        if (measurSheet['A1']['t'] == 's' && measurSheet['B1'] == undefined)
        return true;
      }
      return false;
    }
    catch (e) { 
      return false;
    }
  }
  
  extractBioLogicsExcel(fileName, data) {
    const returnValue = this.getNewReturnValue();
    const dataConvert = new Uint8Array(data);
    const dataExtract = this.getDataFromFileName(fileName, reXLSXExt); //Extract data from the file name
    const workbook = XLSX.read(dataConvert, { type: 'array' });
    for (const measurSheet of Object.values(workbook.Sheets)) {
        const alphabet = 'ABCDEFGHIJKLMNOPQRSTUVWXYZ'
        for (let i = 1; i <= 13; i++) { // Loop through alphabet (columns in excel)
            const xCol = alphabet[2 * i - 2];
            const yCol = alphabet[2 * i - 1];
            if (measurSheet[yCol + String(2)] == undefined){ // No more columns to read
                break;
            }

            const title = measurSheet[xCol + String(1)].v;
            const xType = measurSheet[xCol + String(2)].v;
            const yType = measurSheet[yCol + String(2)].v;

            let xData = [];
            let yData = [];

            for (let j = 3; j <= 100000; j++) {
                if (measurSheet[yCol + String(j)] == undefined) { break; }
                xData.push(measurSheet[xCol + String(j)].v)
                yData.push(measurSheet[yCol + String(j)].v)
            }

            const t = xType.includes('/s') ? xData : [];
            let E = t.length === 0 && xType.includes('/V vs. ') ? xData : []
            let I = t.length === 0 && xType.includes('/mA') ? xData : []

            E = E.length === 0 && yType.includes('/V vs. ') ? yData : E
            I = I.length === 0 && yType.includes('/mA') ? yData : I

            returnValue.processedData.push({
                title,
                group: dataExtract.group,
                concUnit: dataExtract.concUnit,
                time: t,
                potential: E,
                current: I,
                fileName: fileName,
                date: ''
            });
        }
    }
    returnValue.lastError = returnValue.processedData.length === 0 ? `File '${fileName}' is empty` : '';
    returnValue.dataType = DATA_TYPE_DATA;
    returnValue.type = TYPE_PSSESSION;
    returnValue.icon = iconXLSX;
    returnValue.success = returnValue.processedData.length > 0;
    return returnValue;
  }
}

export{
  XLSXExtractor,
};
